/**
 * Words for the advice document that the owner's review of 9 Oct 2026 asked for: the Age Pension
 * page's home treatment said from the arrangement the person is actually in (it read "your former
 * home" to a village resident who has none), the estate page's "worth considering" points where
 * nothing is missing (the actions box printed empty), and the one line the executive summary keeps
 * when a strategy was set aside (the box itself now sits with the alternatives).
 */
import type { Residency } from '../../../../shared/src/calculation/householdPosition.ts';

const DEPOSIT_RULE =
    'A refundable accommodation deposit is not counted as an asset for the pension and is not deemed to earn income, '
    + 'so money moved from the bank or investments into the deposit can raise the pension. '
    + 'Any assets sold to fund the deposit are assessed on what remains.';

export interface HomeRuleInput {
    /** Where the person entering care lives today, as the profile records it. */
    arrangement: Residency | undefined;
    /** The person entering care, by first name. */
    name: string;
    /** For a couple where one enters care: the one who stays, and where they stay. */
    partnerName?: string | null;
    partnerStays?: boolean;
    partnerArrangement?: Residency | undefined;
}

/**
 * "What changes in rules" on the Age Pension page: how the home is treated on entering care, said
 * from the arrangement recorded — a home, a village unit, a granny flat, a rented place, a family
 * home that is someone else's, or a care home already.
 */
export function homeRuleWords(i: HomeRuleInput): string {
    const partner = i.partnerName || 'your partner';
    if (i.partnerStays) {
        switch (i.partnerArrangement || 'own_home') {
            case 'own_home':
                return `The home is not counted in the assets test while ${partner} lives in it, and ${i.name} is assessed as a homeowner. ${DEPOSIT_RULE}`;
            case 'retirement_village':
                return `The village unit is the couple's home while ${partner} lives there: the entry contribution is not counted, and ${i.name} is assessed as a homeowner. ${DEPOSIT_RULE}`;
            case 'granny_flat':
                return `The granny flat is the couple's home while ${partner} lives there, so it is not counted. ${DEPOSIT_RULE}`;
            default:
                return `You own no home, so you are assessed as non-homeowners with the higher asset free area; that does not change when ${i.name} enters care. ${DEPOSIT_RULE}`;
        }
    }
    switch (i.arrangement || 'own_home') {
        case 'retirement_village':
            return 'Your village unit counts as your home today, so you are assessed as a homeowner and the entry contribution is not counted. '
                + 'Once the village pays out your interest you are assessed as a non-homeowner: the payout is counted as an asset and deemed to earn income, '
                + `and the higher non-homeowner asset free area applies. ${DEPOSIT_RULE}`;
        case 'granny_flat':
            return 'Your granny flat interest is treated as your home today, so it is not counted. '
                + 'For up to two years after you enter care it is disregarded in the same way as a former home; after that, what is counted depends on the '
                + `terms of the arrangement and is for Services Australia to assess. ${DEPOSIT_RULE}`;
        case 'renting':
            return 'You rent, so you are assessed as a non-homeowner with the higher asset free area, and that does not change when you enter care. '
                + `Rent Assistance stops once you are a permanent resident. ${DEPOSIT_RULE}`;
        case 'with_family':
            return 'You live with family and own no home, so you are assessed as a non-homeowner with the higher asset free area; '
                + `that does not change when you enter care. ${DEPOSIT_RULE}`;
        case 'aged_care':
            return `You are already in residential care, so this advice brings no change in how your home is treated; your current assessment carries on. ${DEPOSIT_RULE}`;
        default:
            return 'Your former home is disregarded from the assets test for up to two years after you enter care. '
                + `${DEPOSIT_RULE} If the home is rented, the rent is assessable income.`;
    }
}

export interface EstateWorthConsideringInput {
    /** The deposit the plan pays, if any. */
    radPaid: number;
    /** A village unit the plan waits on, or has paid out. */
    leavesVillage: boolean;
    /** Super or an account-based pension is held. */
    hasSuper: boolean;
    /** An Enduring Power of Attorney is recorded as in place. */
    hasEpoa: boolean;
    /** The plan sells something to fund the deposit. */
    planSells: boolean;
    moneyWords: (n: number) => string;
}

/**
 * The estate page's points where nothing is missing from the documents: what this plan does to the
 * estate in practice. Facts, not actions — the actions box stays for the gaps.
 */
export function estateWorthConsidering(i: EstateWorthConsideringInput): string[] {
    const out: string[] = [];
    if (i.radPaid > 0) {
        out.push(`The refundable deposit of ${i.moneyWords(i.radPaid)} comes back to the estate when care ends. The home must refund it within 14 days of being shown probate or letters of administration, less anything the agreement let it draw from the deposit. The estate figures in this document count that refund.`);
    }
    if (i.leavesVillage) {
        out.push('If death comes before the village has paid out the unit, the payment is made to the estate on the same terms, so the timing of the village payout still matters to the family.');
    }
    if (i.hasSuper) {
        out.push('Super and account-based pension money left to adult children who are not financial dependants is taxed on its taxable component (15% plus the Medicare levy). Check that the nominations still say what you want once the plan has moved money.');
    }
    if (i.hasEpoa) {
        out.push('Once in care, the attorney under the Enduring Power of Attorney is the one who deals with the home, Services Australia and the deposit on your behalf. Make sure the home and Services Australia each hold a copy.');
    }
    if (i.planSells) {
        out.push('If a Will names something this plan sells (a particular account, the home), review the Will so that gift does not simply fail.');
    }
    out.push('Review the Will, powers of attorney and nominations after major family, health or asset changes.');
    return out;
}

/** The executive summary's one line where a strategy was set aside; the box is with the alternatives. */
export function horizonSetAsideSummaryLine(count: number): string {
    return count === 1
        ? 'One strategy that would have scored higher on a ranking was set aside because of age and health. It is explained under Alternative strategies considered.'
        : `${count} strategies that would have scored higher on a ranking were set aside because of age and health. They are explained under Alternative strategies considered.`;
}

/**
 * The sentence under the situation page's tiles that bridges today's position to the in-care
 * baseline the strategies are measured against.
 */
export function situationBridgeWords(i: { cashflow: number; estate: number; years: number; moneyWords: (n: number) => string }): string {
    const cf = `${i.cashflow < 0 ? '−' : '+'}${i.moneyWords(Math.abs(Math.round(i.cashflow)))}`;
    return `Moving into care changes this. With no plan, the cashflow becomes ${cf} a year and the estate after ${i.years} ${i.years === 1 ? 'year' : 'years'} ${i.moneyWords(Math.round(i.estate))}. The strategies in this document are measured against that.`;
}
